// src/App.js
import React, { useState, useEffect } from 'react';
import './App.css';
import SearchBar from './components/BookFinder/SearchBar';
import BookList from './components/BookFinder/BookList';
import GenreSelector from './components/BookFinder/GenreSelector';
import LoginPage from './components/LoginPage';
import ProfilePage from './components/ProfilePage';
import BookDetailsPage from './components/BookDetailsPage';
import GenrePage from './components/GenrePage';

// Helper function to create consistent book objects from different API sources
const normalizeBookData = (book, source) => {
  if (source === 'search') {
    return { key: book.key, title: book.title, authors: book.author_name || ['Unknown Author'], cover_id: book.cover_i, publish_year: book.first_publish_year };
  }
  if (source === 'genre') {
    return { key: book.key, title: book.title, authors: book.authors ? book.authors.map(a => a.name) : ['Unknown Author'], cover_id: book.cover_id, publish_year: book.first_published };
  }
  return book;
};


function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('books');
  const [books, setBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState(null);

  // This hook runs once on app load to check for a logged-in user
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const storedUser = localStorage.getItem('bookFinderUser');
    if (storedUser) {
      setCurrentUser(JSON.parse(storedUser));
      if (books.length === 0 && !selectedBook && !selectedGenre) {
        fetchHomepageFeed();
      }
    }
  }, []);

  const handleLogin = (userData) => {
    setCurrentUser(userData);
    localStorage.setItem('bookFinderUser', JSON.stringify(userData));
    fetchHomepageFeed();
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('bookFinderUser');
    setBooks([]);
    setMessage('');
    setSelectedBook(null);
    setSelectedGenre(null);
  };

  const handleBookSelect = async (book) => {
    setIsLoading(true);
    setSelectedBook(null);
    try {
      const response = await fetch(`https://openlibrary.org${book.key}.json`);
      const details = await response.json();
      const description = details.description ? (typeof details.description === 'string' ? details.description : details.description.value) : 'No description available for this book.';
      setSelectedBook({ ...book, description: description });
    } catch (error) {
      console.error("Error fetching book details: ", error);
      setSelectedBook(book); 
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHomepageFeed = async () => {
    setIsLoading(true);
    setBooks([]);
    setMessage('');
    const featuredGenres = ['fantasy', 'science_fiction', 'romance', 'mystery'];
    try {
      const promises = featuredGenres.map(genre => 
        fetch(`https://openlibrary.org/subjects/${genre}.json?limit=8`).then(res => res.json())
      );
      const results = await Promise.all(promises);
      let combinedBooks = [];
      results.forEach(result => {
        if (result.works) {
          const normalized = result.works.map(book => normalizeBookData(book, 'genre'));
          combinedBooks.push(...normalized);
        }
      });
      for (let i = combinedBooks.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [combinedBooks[i], combinedBooks[j]] = [combinedBooks[j], combinedBooks[i]];
      }
      setBooks(combinedBooks);
    } catch (error) {
      console.error("Error fetching homepage feed:", error);
      setMessage("Could not load featured books. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBooksByTitle = async (query) => {
    setSelectedGenre(null);
    setSelectedBook(null);
    if (!query) { return; }
    setIsLoading(true); setBooks([]); setMessage('');
    try {
      const response = await fetch(`https://openlibrary.org/search.json?title=${query}`);
      const data = await response.json();
      if (data.docs.length === 0) setMessage('No books found. Try another title!');
      const normalizedBooks = data.docs.map(book => normalizeBookData(book, 'search'));
      setBooks(normalizedBooks);
    } catch (error) { console.error("Error fetching data: ", error); }
    finally { setIsLoading(false); }
  };
  
  const handleGenreSelect = (genreValue) => {
    setBooks([]);
    setMessage('');
    setSelectedGenre(genreValue);
  };
  
  // --- RENDER LOGIC ---
  if (!currentUser) return <LoginPage onLogin={handleLogin} />;
  
  if (selectedBook) return <BookDetailsPage book={selectedBook} onBack={() => setSelectedBook(null)} />;

  if (selectedGenre) return <GenrePage genre={selectedGenre} onBack={() => setSelectedGenre(null)} onBookSelect={handleBookSelect} />;

  return (
    <div className="app-container">
      {currentPage === 'profile' ? (
        <ProfilePage user={currentUser} onBack={() => setCurrentPage('books')} />
      ) : (
        <>
          <header className="app-header">
            <div className="header-top">
              <span>Welcome, {currentUser.username}!</span>
              <div className="nav-buttons">
                <button onClick={() => setCurrentPage('profile')}>Profile</button>
                <button onClick={handleLogout}>Logout</button>
              </div>
            </div>
            <h1>BookWorld</h1>
            <p>Your Portal to Infinite Stories</p>
            <SearchBar onSearch={fetchBooksByTitle} isSearching={isLoading} />
            <GenreSelector onGenreChange={handleGenreSelect} />
          </header>
          
          <main className="results-container">
            {isLoading && <p className="info-message">Loading the library...</p>}
            {message && !isLoading && <p className="info-message">{message}</p>}
            {!isLoading && <BookList books={books} onBookSelect={handleBookSelect} />}
          </main>
        </>
      )}
    </div>
  );
}

export default App;